<template>
  <div class="handle-assistant-container">
    <!-- Floating button to toggle the chat interface -->
    <div class="fixed bottom-6 right-0 z-50">
      <button
        @click="toggleChat"
        class="group handle-button flex h-16 pl-4 pt-7 w-12 rounded-l-full bg-eggplant-600 hover:bg-eggplant-700 hover:w-20 text-white shadow-lg overflow-hidden transition-all duration-300"
      >
        <SparkleAnim class="h-6 w-6" />
        <span
          class="absolute top-3 left-8 text-white text-sm leading-4 text-right opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >Quick <br />Ask</span
        >
      </button>
    </div>

    <!-- Chat Modal -->
    <Teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isChatOpen"
          class="fixed inset-0 bg-slate-300/90 z-[99999] flex items-center justify-center p-4"
          @click="closeModal"
        >
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="isChatOpen"
              class="relative max-w-5xl w-full h-[calc(100vh-128px)] bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden flex flex-col"
              @click.stop
            >
              <!-- AI Gated State (org members only — external null-org users always pass) -->
              <div
                v-if="!(authStore.isExternalUser || authStore.isAiEnabled)"
                class="flex flex-col items-center justify-center flex-1 px-6 py-12"
              >
                <div
                  class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-900/20"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-7 w-7 text-brand-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">AI Chat</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm text-center">
                  Unlock AI-powered chat to get intelligent answers about your cases, campaigns, and
                  firm data.
                </p>
                <a
                  href="mailto:support@handled.co?subject=AI%20Features%20Inquiry"
                  class="inline-flex items-center rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
                >
                  Contact Support
                </a>
                <button
                  @click="closeModal"
                  class="mt-4 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>

              <!-- Context Selection Phase -->
              <HandledContext
                v-else-if="!isContextSelected"
                @close="toggleChat"
                @start-conversation="handleContextSelected"
              />

              <!-- Chat Interface Phase -->
              <template v-else>
                <!-- Modal Header -->
                <div
                  class="flex items-center justify-between p-4 pb-2 bg-eggplant-50/50 dark:bg-gray-800/30"
                >
                  <div class="flex items-center gap-3">
                    <SparkleAnim class="h-6 w-6 text-eggplant-500" />
                    <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                      Quick Ask<span class="font-normal text-base">{{ contextDisplayText }}</span>
                    </h3>
                  </div>
                  <div class="flex items-center space-x-2">
                    <!-- Conversation history toggle -->
                    <button
                      @click="showConversationList = !showConversationList"
                      class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
                      title="Conversation history"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                    <!-- New conversation -->
                    <button
                      @click="handleNewConversation"
                      class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
                      title="New conversation"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                    <!-- Close -->
                    <button
                      @click="closeModal"
                      class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Main content area -->
                <div class="flex flex-1 min-h-0">
                  <!-- Conversation list sidebar -->
                  <transition
                    enter-active-class="transition-all duration-200 ease-out"
                    enter-from-class="w-0 opacity-0"
                    enter-to-class="w-72 opacity-100"
                    leave-active-class="transition-all duration-150 ease-in"
                    leave-from-class="w-72 opacity-100"
                    leave-to-class="w-0 opacity-0"
                  >
                    <div
                      v-if="showConversationList"
                      class="w-72 border-r border-gray-200 dark:border-gray-700 flex-shrink-0 overflow-hidden"
                    >
                      <ConversationList
                        @new-conversation="handleNewConversation"
                        @select-conversation="handleSelectConversation"
                      />
                    </div>
                  </transition>

                  <!-- Chat area -->
                  <div class="flex-1 flex flex-col min-w-0">
                    <!-- Chat messages -->
                    <div
                      ref="chatMessagesRef"
                      class="flex-1 p-12 overflow-y-auto space-y-8 scroll-smooth"
                    >
                      <!-- Message list -->
                      <div v-for="message in chatStore.messages" :key="message.id">
                        <!-- User message -->
                        <div v-if="message.role === 'user'" class="max-w-[350px] ml-auto mb-6">
                          <div
                            class="px-4 py-3 ml-auto rounded-2xl rounded-br-md bg-gray-100 dark:bg-gray-800"
                          >
                            <p class="text-base text-gray-900 dark:text-gray-100">
                              {{ message.content }}
                            </p>
                          </div>
                        </div>

                        <!-- Assistant message -->
                        <div
                          v-else-if="message.role === 'assistant' && message.content"
                          class="mb-6"
                        >
                          <!-- Persisted work-log trail of what was queried -->
                          <ChatWorkLog v-if="message.activity" :steps="message.activity" />
                          <div
                            class="text-base text-gray-800 dark:text-white/90 leading-relaxed message-content"
                            v-html="renderChatMarkdown(message.content)"
                          ></div>
                        </div>
                      </div>

                      <!-- Live activity + streaming answer (in-progress) -->
                      <div v-if="chatStream.streaming.value || chatStore.isLoading" class="mb-6">
                        <!-- Thinking trail: the model's reasoning before each tool
                             step, kept visible so it never flickers. Rendered in
                             the SAME style as the answer — it's all the agent's
                             side of the conversation. -->
                        <div
                          v-for="(thought, i) in chatStream.thinkingTrail.value"
                          :key="i"
                          class="text-base text-gray-800 dark:text-white/90 leading-relaxed message-content mb-3"
                          v-html="renderChatMarkdown(thought)"
                        ></div>

                        <!-- Work log: the real tool calls, as plain lines -->
                        <ChatWorkLog :steps="chatStream.activitySteps.value" />

                        <!-- Streaming text (in-progress thought or the final answer) -->
                        <template v-if="chatStream.currentText.value">
                          <div
                            class="text-base text-gray-800 dark:text-white/90 leading-relaxed message-content"
                            v-html="renderChatMarkdown(chatStream.currentText.value)"
                          ></div>
                          <span
                            class="inline-block w-2 h-5 bg-eggplant-500 animate-pulse ml-0.5 align-text-bottom rounded-sm"
                          ></span>
                        </template>

                        <!-- Otherwise a steady indicator: "Thinking..." only at the
                             very start; just the pulse between thoughts (the trail
                             above already shows what it's been reasoning about). -->
                        <template v-else>
                          <div
                            v-if="!chatStream.thinkingTrail.value.length"
                            class="text-base text-gray-700 dark:text-gray-300 mb-2"
                          >
                            <span>Thinking...</span>
                          </div>
                          <DotPulseLoader color="#8B5CF6" :customColor="true" />
                        </template>
                      </div>

                      <!-- Error message -->
                      <div
                        v-if="chatStore.error"
                        class="px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                      >
                        <p class="text-sm text-red-600 dark:text-red-400">
                          {{ chatStore.error }}
                        </p>
                      </div>
                    </div>

                    <!-- Chat input area -->
                    <div class="px-12 pb-8">
                      <div class="relative">
                        <input
                          ref="chatInputRef"
                          v-model="newMessage"
                          type="text"
                          placeholder="Ask Handled anything..."
                          class="w-full p-4 pr-12 text-base text-gray-800 bg-gray-50 placeholder:text-gray-400 dark:bg-gray-900 dark:text-white/90 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-eggplant-400 focus:outline-hidden focus:ring-3 focus:ring-eggplant-500/10 dark:focus:border-eggplant-800 dark:placeholder:text-white/30"
                          :disabled="chatStore.isLoading || chatStream.streaming.value"
                          @keydown.enter.prevent="handleSubmit"
                        />
                        <button
                          type="button"
                          :disabled="
                            !newMessage.trim() || chatStore.isLoading || chatStream.streaming.value
                          "
                          class="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-md bg-eggplant-600 hover:bg-eggplant-700 active:bg-eggplant-800 text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          @click="handleSubmit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </transition>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from "vue";

import ChatWorkLog from "@/components/chat/ChatWorkLog.vue";
import ConversationList from "@/components/chat/ConversationList.vue";
import DotPulseLoader from "@/components/chat/DotPulseLoader.vue";
import HandledContext from "@/components/chat/HandledContext.vue";
import SparkleAnim from "@/components/chat/SparkleAnim.vue";
import { useChatStream } from "@/composables/useChatStream";
import { useCampaigns } from "@/queries/campaigns";
import { useAuthStore } from "@/stores/auth";
import { useChatStore } from "@/stores/chatStore";
import { piiLaneFor } from "@/utils/piiLane";
import { renderChatMarkdown } from "@/utils/renderChatMarkdown";

// ---------------------------------------------------------------------------
// Stores & composables
// ---------------------------------------------------------------------------

const chatStore = useChatStore();
const chatStream = useChatStream();
const authStore = useAuthStore();
// Disabled for portal funders inside `useCampaigns` (RAP-667): they get firm chat only.
const campaignsQuery = useCampaigns();
const allCampaigns = computed(() => campaignsQuery.data.value ?? []);

// ---------------------------------------------------------------------------
// Refs
// ---------------------------------------------------------------------------

const isChatOpen = ref(false);
const newMessage = ref("");
const chatMessagesRef = ref<HTMLElement | null>(null);
const chatInputRef = ref<HTMLInputElement | null>(null);
const showConversationList = ref(false);

// Context selection state
const isContextSelected = ref(false);
const selectedContext = ref<{
  campaignId?: string;
  caseId?: string;
  caseName?: string;
  data?: Record<string, unknown>;
  step: string;
  type: "campaign" | "case" | "firm" | null;
} | null>(null);

// Context display data
const contextDisplayData = ref<{
  campaignName?: string;
  caseName?: string;
}>({});

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const contextDisplayText = computed(() => {
  if (!selectedContext.value) return "";
  const ctx = selectedContext.value;

  if (ctx.type === "case") {
    const caseName = contextDisplayData.value.caseName || "Unknown Case";
    const campaignName = contextDisplayData.value.campaignName || "";
    return ` -- ${caseName}${campaignName ? `, ${campaignName}` : ""}`;
  } else if (ctx.type === "campaign") {
    const campaignName = contextDisplayData.value.campaignName || "Unknown Campaign";
    return ` -- ${campaignName}`;
  } else if (ctx.type === "firm") {
    // External null-org users don't have "their firm" — they have campaigns
    // shared with them. Use a label that fits both classes.
    return authStore.isExternalUser ? " -- Shared Campaigns" : " -- Firm Assistant";
  }
  return "";
});

// ---------------------------------------------------------------------------
// Chat actions
// ---------------------------------------------------------------------------

async function handleSubmit() {
  if (!newMessage.value.trim() || chatStore.isLoading || chatStream.streaming.value) {
    return;
  }

  const messageText = newMessage.value.trim();
  newMessage.value = "";

  // Refocus input immediately
  nextTick(() => {
    chatInputRef.value?.focus();
  });

  await sendMessage(messageText);
}

async function sendMessage(text: string) {
  if (!chatStore.entityContext) return;

  const ctx = chatStore.entityContext;

  // A sign-out or account switch ($reset) during the stream makes its answer the previous user's.
  const generation = chatStore.generation;

  // Add user message to local store
  chatStore.addUserMessage(text);

  // Set loading state
  chatStore.isLoading = true;
  chatStore.error = null;

  try {
    // Stream the response
    const result = await chatStream.sendMessage({
      conversationId: chatStore.conversationId,
      entityId: ctx.entityId,
      entityType: ctx.entityType,
      question: text,
    });
    if (generation !== chatStore.generation) return;

    // Stream finished -- persist the final text + the work-log trail (copied,
    // since chatStream.activitySteps is reset on the next send) so the user
    // keeps a visual record of what was actually queried.
    if (result.fullText) {
      const activity = [...chatStream.activitySteps.value];
      const placeholderId = chatStore.addAssistantPlaceholder(activity);
      chatStore.updateAssistantMessage(placeholderId, result.fullText);
    }

    // Update conversation ID (set by the done event from the backend)
    if (result.conversationId) {
      chatStore.setConversationId(result.conversationId);
    }

    // Propagate stream error to store if one occurred
    if (chatStream.error.value) {
      chatStore.error = chatStream.error.value;
    }
  } catch {
    if (generation !== chatStore.generation) return;
    chatStore.error = "An unexpected error occurred. Please try again.";
  } finally {
    if (generation === chatStore.generation) chatStore.isLoading = false;
  }
}

// ---------------------------------------------------------------------------
// Conversation management
// ---------------------------------------------------------------------------

function handleNewConversation() {
  chatStore.startNewConversation();
  showConversationList.value = false;

  // Re-add welcome message
  const firstMessage = generateFirstMessage();
  chatStore.initialize(firstMessage);

  nextTick(() => {
    chatInputRef.value?.focus();
  });
}

async function handleSelectConversation(convId: string) {
  await chatStore.loadConversationMessages(convId);
  showConversationList.value = false;

  nextTick(() => {
    scrollToBottom(false);
    chatInputRef.value?.focus();
  });
}

// ---------------------------------------------------------------------------
// Context selection
// ---------------------------------------------------------------------------

async function handleContextSelected(context: {
  campaignId?: string;
  caseId?: string;
  caseName?: string;
  data?: Record<string, unknown>;
  step: string;
  type: "campaign" | "case" | "firm" | null;
}) {
  selectedContext.value = context;
  isContextSelected.value = true;

  // Fetch display names
  await fetchContextDisplayData(context);

  // Set entity context in store
  if (context.type === "case" && context.caseId) {
    chatStore.setEntityContext({
      campaignId: context.campaignId,
      campaignName: contextDisplayData.value.campaignName,
      caseName: contextDisplayData.value.caseName,
      entityId: context.caseId,
      entityType: "case",
      type: "case",
    });
  } else if (context.type === "campaign" && context.campaignId) {
    chatStore.setEntityContext({
      campaignName: contextDisplayData.value.campaignName,
      entityId: context.campaignId,
      entityType: "campaign",
      type: "campaign",
    });
  } else if (context.type === "firm") {
    // Firm entityId derives to orgId for org members, userId for external
    // null-org users. The route layer enforces this contract: org members
    // must send entityId === user.organizationId; null-org callers must
    // send entityId === user.id. Either way the same value flows through
    // chatStore.loadConversations() so the conversation list query
    // auto-aligns.
    const orgId = authStore.getUser?.organizationId ?? null;
    const userId = authStore.getUser?.id ?? "";
    const entityId = orgId ?? userId;
    chatStore.setEntityContext({
      entityId,
      entityType: "firm",
      type: "firm",
    });
  }

  // Initialize with welcome message
  chatStore.startNewConversation();
  const firstMessage = generateFirstMessage();
  chatStore.initialize(firstMessage);

  // Load conversation history for this entity
  chatStore.loadConversations();

  nextTick(() => {
    scrollToBottom();
    chatInputRef.value?.focus();
  });
}

function generateFirstMessage(): string {
  if (!selectedContext.value) {
    return "Hello! I'm Handled, your AI Assistant. How can I help you?";
  }

  const ctx = selectedContext.value;
  if (ctx.type === "case") {
    const caseName = contextDisplayData.value.caseName || "this case";
    const campaignName = contextDisplayData.value.campaignName || "";
    return `What would you like to know about **${caseName}**${campaignName ? `, **${campaignName}**` : ""}?`;
  } else if (ctx.type === "campaign") {
    const campaignName = contextDisplayData.value.campaignName || "this campaign";
    return `What would you like to know about the **${campaignName}** campaign?`;
  } else if (ctx.type === "firm") {
    // External null-org users have no firm — they have campaigns shared
    // with them by partner firms. Phrase the welcome accordingly.
    return authStore.isExternalUser
      ? "What would you like to know about the campaigns shared with you?"
      : "What would you like to know about your firm and its activities?";
  }
  return "Hello! I'm Handled, your AI Assistant. How can I help you?";
}

async function fetchContextDisplayData(context: {
  campaignId?: string;
  caseId?: string;
  caseName?: string;
  type: "campaign" | "case" | "firm" | null;
}) {
  contextDisplayData.value = {};

  try {
    if (context.type === "case" && context.caseId) {
      // Use case name directly if available from HandledContext
      if (context.caseName) {
        contextDisplayData.value.caseName = context.caseName;
      } else {
        contextDisplayData.value.caseName = `Case #${context.caseId}`;
      }

      // Get campaign name
      if (context.campaignId) {
        const campaign = allCampaigns.value.find((c) => c.id === context.campaignId);
        if (campaign) {
          contextDisplayData.value.campaignName = campaign.name;
        }
      }
    } else if (context.type === "campaign" && context.campaignId) {
      const campaign = allCampaigns.value.find((c) => c.id === context.campaignId);
      if (campaign) {
        contextDisplayData.value.campaignName = campaign.name;
      }
    }
  } catch {
    // Silently continue with defaults
  }
}

// ---------------------------------------------------------------------------
// Modal & UI
// ---------------------------------------------------------------------------

function toggleChat() {
  isChatOpen.value = !isChatOpen.value;

  if (!isChatOpen.value) {
    // Closing -- reset context
    isContextSelected.value = false;
    selectedContext.value = null;
    contextDisplayData.value = {};
    showConversationList.value = false;

    chatStore.clearContext();

    // Restore body scrolling
    document.body.style.overflow = "";
  } else {
    // Opening -- prevent background scroll
    document.body.style.overflow = "hidden";

    // Auto-select firm context for users whose org doesn't sync data, and
    // for null-org external users — both classes get the simplified
    // firm-only chat. The firm branch in handleContextSelected derives
    // entityId correctly for org members (orgId) and externals (userId). Funders (no
    // view_pii) are firm-only too, even if their org syncs data: the server 403s case and
    // campaign chat for any org member without view_pii (RAP-696) — so this keys on the missing
    // permission alone, exactly like the server, not on the portal flag.
    const lane = piiLaneFor(authStore.getUser);
    const lacksViewPii = lane === "funder" || lane === "none";
    if (!authStore.isDataSyncEnabled || lacksViewPii) {
      handleContextSelected({
        step: "initial",
        type: "firm",
      });
    }
  }
}

function closeModal() {
  toggleChat();
}

function scrollToBottom(smooth: boolean = true) {
  if (chatMessagesRef.value) {
    const el = chatMessagesRef.value;
    if (smooth) {
      el.scrollTo({ behavior: "smooth", top: el.scrollHeight });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

// The store was reset (sign-out, or a magic-link account switch with no reload): stop streaming
// the previous user's answer into this panel, and drop what it had already streamed.
watch(
  () => chatStore.generation,
  () => chatStream.reset()
);

// Auto-scroll when messages change
watch(
  () => chatStore.messages.length,
  async () => {
    if (isChatOpen.value) {
      await nextTick();
      scrollToBottom();
    }
  }
);

// Auto-scroll during streaming
watch(
  () => chatStream.currentText.value,
  async () => {
    if (isChatOpen.value && chatStream.streaming.value) {
      await nextTick();
      setTimeout(() => scrollToBottom(), 10);
    }
  }
);

// Focus input when loading finishes
watch(
  () => chatStore.isLoading,
  (newVal, oldVal) => {
    if (!newVal && oldVal && isChatOpen.value) {
      nextTick(() => chatInputRef.value?.focus());
    }
  }
);

// Handle ESC key
function handleEscKey(event: KeyboardEvent) {
  if (event.key === "Escape" && isChatOpen.value) {
    closeModal();
  }
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
  chatStream.cancel();
  document.body.style.overflow = "";
});

// ---------------------------------------------------------------------------
// Expose for parent components (e.g., AIChat.vue calls toggleChat via ref)
// ---------------------------------------------------------------------------

defineExpose({ toggleChat });
</script>

<style scoped>
.message-content :deep(p:last-child) {
  margin-bottom: 0;
}

/* Headings: marked emits bare <h1>–<h4> (no injected class). Give them a clear,
   compact hierarchy so section titles read as titles without dwarfing the body. */
.message-content :deep(h1),
.message-content :deep(h2),
.message-content :deep(h3),
.message-content :deep(h4) {
  font-weight: 600;
  line-height: 1.3;
  margin: 1rem 0 0.4rem;
}
.message-content :deep(h1:first-child),
.message-content :deep(h2:first-child),
.message-content :deep(h3:first-child),
.message-content :deep(h4:first-child) {
  margin-top: 0;
}
.message-content :deep(h1) {
  font-size: 1.2em;
}
.message-content :deep(h2) {
  font-size: 1.1em;
}
.message-content :deep(h3),
.message-content :deep(h4) {
  font-size: 1em;
}

/* Loose-list items wrap their text in a <p class="mb-3"> (the model separates
   items with blank lines). That bottom margin double-spaces list items and, with
   the heading text, pushes content off the marker's baseline — strip it so the
   item text stays tight against its marker. */
.message-content :deep(li > p) {
  margin: 0;
}
.message-content :deep(li > p + ul),
.message-content :deep(li > p + ol) {
  margin-top: 0.25rem;
}

/* Markdown tables: the renderer emits a bare <table>, which Tailwind's reset
   leaves border-less and cramped. Give it real structure. Translucent neutral
   borders/tints adapt to both light and dark mode without dark: variants. */
.message-content :deep(table) {
  width: 100%;
  max-width: 100%;
  margin: 0.5rem 0 1rem;
  border-collapse: collapse;
  font-size: 0.95em;
  line-height: 1.4;
}
.message-content :deep(th),
.message-content :deep(td) {
  padding: 0.45rem 0.7rem;
  border: 1px solid rgba(128, 128, 128, 0.25);
  text-align: left;
  vertical-align: top;
  word-break: break-word;
}
.message-content :deep(thead th) {
  font-weight: 600;
  background: rgba(139, 92, 246, 0.08); /* eggplant tint */
}
.message-content :deep(tbody tr:nth-child(even) td) {
  background: rgba(128, 128, 128, 0.05);
}
/* Keep the first column (dates, IDs, short keys) from wrapping mid-value. */
.message-content :deep(th:first-child),
.message-content :deep(td:first-child) {
  white-space: nowrap;
}
</style>
